/**
 * Utility functions for handling tradesperson data
 *
 * This file uses the consolidated tradeCategories.ts as the single source of truth
 * for all trade category related information.
 */
import { Tradesperson } from "./types";
import { LucideIcon } from "lucide-react";
import { getTradeIcon as getCategoryIcon, getTradeDisplayName as getCategoryDisplayName, standardizeTradeType } from "./tradeCategories";
import { debug } from "./utils";

// Using only Lucide icons
type IconComponent = LucideIcon;

/**
 * Gets the display name for a tradesperson
 * @param tradesperson The tradesperson object
 * @returns A string with the most appropriate name
 */
export function getTradeDisplayName(tradesperson: Tradesperson | null | undefined): string {
  if (!tradesperson) return "Unknown Trade";

  // Use only the new fields
  return tradesperson.org || tradesperson.contact || "Unnamed Trade";
}

/**
 * Gets the trade type description
 * @param tradesperson The tradesperson object
 * @returns A string representing the trade type
 */
export function getTradeType(tradesperson: Tradesperson | null | undefined): string {
  if (!tradesperson) return "general contractor";

  // trade_type is the standard field, with category as a fallback
  const tradeType = tradesperson.trade_type || tradesperson.category;

  // Standardize the trade type to ensure it matches our catalog
  return standardizeTradeType(tradeType);
}

/**
 * Gets the category for a tradesperson
 * @param tradesperson The tradesperson object
 * @returns A string representing the category
 * @deprecated Use getTradeType instead, which is the standard field
 */
export function getTradeCategory(tradesperson: Tradesperson | null | undefined): string {
  if (!tradesperson) return "general contractor";

  // Category is the primary field, with trade_type as a fallback
  const category = tradesperson.category || tradesperson.trade_type;

  // Standardize the category to ensure it matches our catalog
  return standardizeTradeType(category);
}

/**
 * Gets the appropriate icon for a tradesperson based on trade type
 * @param tradesperson The tradesperson object
 * @returns A Lucide icon component reference
 */
export function getTradeIconForTrade(tradesperson: Tradesperson | null | undefined): IconComponent {
  if (!tradesperson) {
    // Default icon
    return getCategoryIcon(undefined);
  }

  // Get the standardized trade type first
  const standardizedType = getTradeType(tradesperson);

  // Then get the icon for that type
  return getCategoryIcon(standardizedType);
}

/**
 * Filters tradespeople by category using the centralized keyword definitions
 * @param tradespeople The list of tradespeople to filter
 * @param categoryId The category ID to filter by
 * @returns Filtered list of tradespeople that match the category
 */
export function filterTradesByCategoryId(tradespeople: Tradesperson[], categoryId: string | null): Tradesperson[] {
  // If no category is specified, return all tradespeople
  if (!categoryId || !tradespeople?.length) {
    debug.log(`No category specified or empty tradespeople list. Returning all ${tradespeople?.length || 0} tradespeople.`);
    return tradespeople || [];
  }

  // Get the standardized category name (for consistent matching)
  const categoryName = categoryId.toLowerCase();
  debug.log(`Filtering for category: "${categoryName}"`);

  // Return tradespeople whose trade type or category matches the specified category
  const filtered = tradespeople.filter(tradesperson => {
    if (!tradesperson) return false;

    // Get standardized type (normalized)
    const tradeType = getTradeType(tradesperson)?.toLowerCase() || '';

    // Direct match by trade type/category
    if (tradeType === categoryName) {
      debug.log(`Direct match for "${tradesperson.org || tradesperson.contact}" - trade type "${tradeType}" matches category "${categoryName}"`);
      return true;
    }

    // Check all raw fields for matching terms
    const rawType = (tradesperson.trade_type || '').toLowerCase();
    const rawCategory = (tradesperson.category || '').toLowerCase();

    // Direct string matches
    if (rawType === categoryName || rawCategory === categoryName) {
      debug.log(`Raw field match for "${tradesperson.org || tradesperson.contact}" - raw type "${rawType}" or category "${rawCategory}" matches "${categoryName}"`);
      return true;
    }

    // Check for substring matches (less specific)
    if (rawType.includes(categoryName) || rawCategory.includes(categoryName)) {
      debug.log(`Substring match for "${tradesperson.org || tradesperson.contact}" - raw type "${rawType}" or category "${rawCategory}" contains "${categoryName}"`);
      return true;
    }

    // For roofers, ensure we also match gutter-related terms
    if (categoryName === 'roofer' && /roof|gutter|fascia|soffit/i.test(rawType)) {
      debug.log(`Roofer-specific match for "${tradesperson.org || tradesperson.contact}" - contains gutter-related terms in "${rawType}"`);
      return true;
    }

    return false;
  });

  debug.log(`Filtered ${tradespeople.length} tradespeople to ${filtered.length} for category "${categoryName}"`);
  return filtered;
}
